import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { classNames, getUniqueId } from "@swa-ui/string";

import { areaPropTypes } from "../Area";
import { AriaLive } from "../AriaLive";
import { Caption } from "../Caption";
import { keyCodes } from "../defines/keyCodes";
import { HorizontalList, horizontalListPropTypes } from "../HorizontalList";
import { Icon } from "../Icon";
import { SelectionMarker } from "../SelectionMarker";
import styles from "./SelectList.module.scss";

const PADDING = 8;
const SELECTLIST_DISABLED_DESIGN_TOKEN = "cmp-core-color-select-list-disabled";
const SELECTLIST_ICON_DESIGN_TOKEN = "cmp-core-color-select-list-icon";

/**
 * SelectList provides a flyout component to render a list of vertical options and a set of
 * horizontal options at the bottom of the flyout.
 */
export const SelectList = React.forwardRef((props, ref) => {
  const {
    alignment = "left",
    className,
    disabled = false,
    emptyContent,
    emptyContentDisplayValue,
    heading,
    items = [],
    minimumListWidth = "medium",
    noSelectionDisplayValue,
    onChange,
    onClick,
    onRevealChange,
    options = [],
    readOnly = false,
    revealed = false,
    showBorder = true,
    value,
  } = props;
  const [captionState, setCaptionState] = useState("hidden");
  const [currentValue, setCurrentValue] = useState(
    value || (items[getInitialHighlightIndex()]?.value ?? "")
  );
  const [, setHasFocus] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(getInitialHighlightIndex());
  const [itemHeights, setItemHeights] = useState([]);
  const [liveRegionText, setLiveRegionText] = useState("");
  const [selectionMade, setSelectionMade] = useState(false);
  const componentId = useRef(name || getUniqueId("select-list-id"));
  const containerRef = useRef();
  const contentsRef = useRef();
  const headingRef = useRef();
  const listRef = useRef();
  const optionsRef = useRef();
  const triggerRef = ref || useRef();

  useEffect(() => {
    revealed ? showCaption() : hideCaption();
  }, [revealed]);

  useEffect(() => {
    const newValueIndex = items.findIndex((item) => item.value === value);

    if (newValueIndex >= 0 && value !== currentValue) {
      setNewSelection(newValueIndex);
    }
  }, [value]);

  return (
    <div {...getContainerProps()}>
      <Caption {...getCaptionProps()}>{renderTrigger()}</Caption>
      <AriaLive>{liveRegionText}</AriaLive>
    </div>
  );

  function renderTrigger() {
    return (
      <div {...getTriggerProps()}>
        <div>{getSelectedLabel()}</div>
        <Icon {...getIconProps()} />
      </div>
    );
  }

  function renderList() {
    return (
      <div className={styles.captionContent} onBlur={handleBlur}>
        {heading && renderHeading()}
        <div {...getOuterContainerProps()}>
          {items?.length ? renderItems() : renderEmptyMessage()}
        </div>
        {renderOptions()}
      </div>
    );
  }

  function renderHeading() {
    return <div {...getHeadingContainerProps()}>{heading}</div>;
  }

  function renderEmptyMessage() {
    return <div className={getContentClass()}>{emptyContent}</div>;
  }

  function renderItems() {
    return (
      <>
        <SelectionMarker {...getSelectionMarkerProps()} />
        {renderListItems()}
      </>
    );
  }

  function renderListItems() {
    return <ul {...getListProps()}>{items.map(renderListItem)}</ul>;
  }

  function renderListItem(item, index) {
    return <li {...getListItemProps(item, index)}>{item.label}</li>;
  }

  function renderOptions() {
    return options.length > 0 ? (
      <div className={styles.options} onMouseDown={handleOptionsMouseDown} ref={optionsRef}>
        <div onClick={handleOptionClick} onFocus={(event) => event.stopPropagation()}>
          <HorizontalList {...getOptionsProps()} />
        </div>
      </div>
    ) : null;
  }

  function getContainerProps() {
    return {
      className,
      onKeyDown: handleKeyDown,
      ref: contentsRef,
    };
  }

  function getCaptionProps() {
    return {
      adjoiningContent: renderList(),
      alignment,
      id: `${componentId.current}-caption`,
      location: disabled || readOnly ? "hidden" : captionState,
      mainClassName: getMainContentClass(),
    };
  }

  function getTriggerProps() {
    return {
      "aria-activedescendant":
        captionState !== "hidden" ? `${getItemId(highlightIndex)}` : undefined,
      "aria-controls": `${componentId.current}-caption`,
      "aria-describedby": props["aria-describedby"],
      "aria-expanded": captionState !== "hidden",
      "aria-label": props["aria-label"],
      "aria-owns": `${componentId.current}-list`,
      className: getTriggerClass(),
      onBlur: handleBlur,
      onClick,
      onFocus: handleFocus,
      onKeyDown: disabled ? undefined : handleTriggerKeyDown,
      onMouseDown: disabled ? undefined : handleTriggerMouseDown,
      ref: triggerRef,
      role: "combobox",
      tabIndex: disabled ? undefined : 0,
    };
  }

  function getIconProps() {
    return {
      className: styles.indicator,
      color: disabled ? SELECTLIST_DISABLED_DESIGN_TOKEN : SELECTLIST_ICON_DESIGN_TOKEN,
      custom: { type: captionState === "hidden" ? "active" : "inactive" },
      name: "ArrowThin",
      role: "presentation",
    };
  }

  function getOuterContainerProps() {
    return {
      className: styles.outerContainer,
      ref: containerRef,
    };
  }

  function getHeadingContainerProps() {
    return {
      className: styles.heading,
      onMouseDown: (event) => {
        event.stopPropagation();
        event.preventDefault();
      },
      ref: headingRef,
    };
  }

  function getSelectionMarkerProps() {
    return {
      height: isFocusOnList() ? itemHeights?.[highlightIndex] ?? 0 : 0,
      yOffset: Math.max(getOffsetFromIndex(highlightIndex), 0) + PADDING + getHeadingHeight(),
    };
  }

  function getOptionsProps() {
    return {
      className: styles.options,
      items: options,
      separator: false,
      spacing: "small",
    };
  }

  function getListProps() {
    return {
      className: getListClass(),
      id: `${componentId.current}-list`,
      onKeyDown: handleListKeyDown,
      onMouseMove: handleMouseMove,
      ref: listRef,
      role: "listbox",
      tabIndex: 0,
    };
  }

  function getListItemProps(listItem, index) {
    return {
      "aria-selected": index === highlightIndex,
      className: getListItemClass(listItem),
      id: `${getItemId(index)}`,
      key: listItem.value,
      onClick: handleItemClick.bind(this, index),
      ref: registerItem(index),
      role: "option",
    };
  }

  function registerItem(index) {
    return (element) => {
      if (element) {
        const newItemHeight = element.getBoundingClientRect().height;

        if (newItemHeight !== itemHeights[index]) {
          setItemHeights((previousItemHeights) => {
            const newItemHeights = [...previousItemHeights];

            newItemHeights[index] = newItemHeight;

            return newItemHeights;
          });
        }
      }
    };
  }

  function getTriggerClass() {
    return classNames(styles.trigger, {
      [styles.disabled]: disabled,
      [styles.focus]: isFocusOnTrigger() || isFocusOnTriggerChild(),
    });
  }

  function getHeadingHeight() {
    return headingRef?.current?.getBoundingClientRect().height ?? 0;
  }

  function isFocusOnList() {
    return document.activeElement === listRef?.current;
  }

  function isFocusOnTrigger() {
    return document.activeElement === getTriggerRef();
  }

  function isFocusOnTriggerChild() {
    return listRef?.current?.contains(document.activeElement);
  }

  function getListItemClass(listItem) {
    return classNames(styles.dropDownListItem, listItem.className, {
      [styles.disabled]: listItem.disabled,
    });
  }

  function getListClass() {
    return classNames(styles.list, getContentClass());
  }

  function getContentClass() {
    return classNames({
      [styles.minimumListWidthLarge]: minimumListWidth === "large",
      [styles.minimumListWidthMedium]: minimumListWidth === "medium",
      [styles.minimumListWidthSmall]: minimumListWidth === "small",
      [styles.minimumListWidthXlarge]: minimumListWidth === "xlarge",
      [styles.minimumListWidthXsmall]: minimumListWidth === "xsmall",
    });
  }

  function getMainContentClass() {
    return classNames({
      [styles.borderedMainContent]: showBorder,
      [styles.mainContent]: true,
    });
  }

  function handleKeyDown(event) {
    const { key } = event;
    const actionableKeys = [
      keyCodes.KEY_DOWN,
      keyCodes.KEY_END,
      keyCodes.KEY_HOME,
      keyCodes.KEY_LEFT,
      keyCodes.KEY_PAGE_DOWN,
      keyCodes.KEY_PAGE_UP,
      keyCodes.KEY_RIGHT,
      keyCodes.KEY_UP,
    ];

    if (key === keyCodes.KEY_ESCAPE) {
      processEscape();
      focusTrigger();
    } else if (actionableKeys.indexOf(key) !== -1) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  function focusTrigger() {
    getTriggerRef().focus();
  }

  function getTriggerRef() {
    return triggerRef?.current || triggerRef;
  }

  function handleTriggerKeyDown(event) {
    const { key } = event;
    const actionableKeys = [
      keyCodes.KEY_DOWN,
      keyCodes.KEY_END,
      keyCodes.KEY_ENTER,
      keyCodes.KEY_ESCAPE,
      keyCodes.KEY_HOME,
      keyCodes.KEY_LEFT,
      keyCodes.KEY_PAGE_DOWN,
      keyCodes.KEY_PAGE_UP,
      keyCodes.KEY_RIGHT,
      keyCodes.KEY_SPACE,
      keyCodes.KEY_UP,
    ];

    if (actionableKeys.indexOf(key) !== -1 && captionState === "hidden") {
      openCaption();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  function handleListKeyDown(event) {
    const { key } = event;

    if (key === keyCodes.KEY_DOWN || key === keyCodes.KEY_RIGHT) {
      processDown(event);
    } else if (key === keyCodes.KEY_ENTER) {
      processEnter(event);
    } else if (key === keyCodes.KEY_SPACE) {
      processEnter(event);
    } else if (key === keyCodes.KEY_END || key === keyCodes.KEY_PAGE_DOWN) {
      processEnd(event);
    } else if (key === keyCodes.KEY_HOME || key === keyCodes.KEY_PAGE_UP) {
      processHome(event);
    } else if (key === keyCodes.KEY_LEFT || key === keyCodes.KEY_UP) {
      processUp(event);
    }
  }

  function handleTriggerMouseDown() {
    if (captionState === "hidden" && !readOnly) {
      openCaption();
    } else {
      hideCaption();
    }
  }

  function handleOptionsMouseDown(event) {
    if (!optionsRef.current.contains(event.relatedTarget)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  function handleBlur(event) {
    setHasFocus(false);

    if (!contentsRef.current.contains(event.relatedTarget)) {
      hideCaption();
    }
  }

  function handleFocus(event) {
    setHasFocus(true);

    if (!contentsRef.current.contains(event.relatedTarget)) {
      openCaption();
    }
  }

  function handleItemClick(index, event) {
    const listItem = items[index];

    if (listItem.disabled) {
      event.preventDefault();
    } else {
      setNewSelection(index);
      delayCloseCaption();
    }
  }

  function handleOptionClick() {
    delayCloseCaption();
  }

  function handleMouseMove(event) {
    const mousePosition = event.clientY - containerRef.current.getBoundingClientRect().top;
    const newHighlightIndex = findIndexFromOffset(mousePosition);

    if (!items[newHighlightIndex]?.disabled) {
      setHighlightIndex(newHighlightIndex);
    }
  }

  function processEscape() {
    delayCloseCaption();
  }

  function processEnter(event) {
    if (captionState === "hidden") {
      showCaption();
    } else {
      setNewSelection(highlightIndex);
      delayCloseCaption();
    }

    event.stopPropagation();
    event.preventDefault();
  }

  function processDown(event) {
    updateHighlightAndAnnounce(getNextHighlightIndex(highlightIndex));
    event.preventDefault();
  }

  function processUp(event) {
    updateHighlightAndAnnounce(getPreviousHighlightIndex(highlightIndex));
    event.preventDefault();
  }

  function processEnd(event) {
    let newHighlightIndex = items.length - 1;

    if (items[newHighlightIndex]?.disabled) {
      newHighlightIndex = getPreviousHighlightIndex(newHighlightIndex);
    }
    updateHighlightAndAnnounce(newHighlightIndex);
    event.preventDefault();
  }

  function processHome(event) {
    let newHighlightIndex = 0;

    if (items[newHighlightIndex]?.disabled) {
      newHighlightIndex = getNextHighlightIndex(0);
    }
    updateHighlightAndAnnounce(newHighlightIndex);
    event.preventDefault();
  }

  function getNextHighlightIndex(index) {
    const itemsBelowHighlight = items.slice(index + 1);
    const workingHighlightIndex = itemsBelowHighlight.findIndex((item) => !item.disabled);
    let newHighlightIndex = index;

    if (workingHighlightIndex !== -1) {
      newHighlightIndex = workingHighlightIndex + index + 1;
    }

    return newHighlightIndex;
  }

  function getPreviousHighlightIndex(index) {
    const itemsAboveHighlight = items.slice(0, index).reverse();
    const workingHighlightIndex = itemsAboveHighlight.findIndex((item) => !item.disabled);
    let newHighlightIndex = index;

    if (workingHighlightIndex !== -1) {
      newHighlightIndex = index - workingHighlightIndex - 1;
    }

    return newHighlightIndex;
  }

  function getInitialHighlightIndex() {
    let index = 0;

    if (items.length && value) {
      index = items.findIndex((item) => item.value === value);
    }

    return index;
  }

  function setNewSelection(newSelectedIndex) {
    const newValue = items[newSelectedIndex]?.value ?? "";

    setSelectionMade(true);
    setHighlightIndex(newSelectedIndex);
    setCurrentValue(newValue);
    onChange?.({ target: { value: newValue } }, newSelectedIndex);
  }

  function getItemId(index) {
    return `${componentId.current}-item-${index}`;
  }

  function getSelectedLabel() {
    const item = items[findIndexFromValue(items, currentValue)];
    let selectedLabel = item?.displayValue ?? emptyContentDisplayValue;

    if (!selectionMade && item?.displayValue && noSelectionDisplayValue) {
      selectedLabel = noSelectionDisplayValue;
    }

    return selectedLabel;
  }

  function getOffsetFromIndex(index) {
    const heights = index > 0 ? itemHeights.slice(0, index) : [];

    return heights.reduce((partialSum, height) => partialSum + height, 0);
  }

  function findIndexFromOffset(offset) {
    const itemCount = items.length;
    let accumulated = 0;

    const index = itemHeights.findIndex((height) => {
      accumulated += height;

      return accumulated > offset;
    });

    return index === -1 ? itemCount - 1 : index;
  }

  function findIndexFromValue(initializedItems, workingValue) {
    return initializedItems.findIndex((item) => item.value === workingValue);
  }

  function delayCloseCaption() {
    focusTrigger();
    hideCaption();
  }

  function hideCaption() {
    setCaptionState("hidden");
    onRevealChange?.(false);
  }

  function openCaption() {
    if (!readOnly) {
      listRef.current?.focus();
      showCaption();
    }
  }

  function showCaption() {
    setCaptionState("below");
    onRevealChange?.(true);
  }

  function updateHighlightAndAnnounce(newIndex) {
    setHighlightIndex(newIndex);
    setLiveRegionText(items[newIndex]?.label || "");
  }
});

SelectList.displayName = "SelectList";

SelectList.propTypes = {
  /**
   * This prop controlls whether the drop-down portion of SelectList is right or left aligned to the
   * trigger portion.
   */
  alignment: PropTypes.oneOf(["left", "right"]),

  /**
   * aria-describedby id to element which provides additional accessibility description of container
   * element.
   */
  "aria-describedby": PropTypes.string,

  /** aria-label text to provide additional accessibility description of container element. */
  "aria-label": PropTypes.string,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Indicates Dropdown should apply disabled styling and ignore mouse and key events. */
  disabled: PropTypes.bool,

  /**
   * Content to be displayed when there are not list items. When given, emptyContentDisplayValue
   * must also be specified.
   */
  emptyContent: PropTypes.node,

  /**
   * Content to be displayed when there are not list items and when emptyContent is rendered.
   * emptyContentDisplayValue is rendered as SelectList's trigger.
   */
  emptyContentDisplayValue: PropTypes.string,

  /** Optional content to be displayed at the top of SelectList's flyout. */
  heading: PropTypes.node,

  /** Array of items to define the options available in SelectList's "main" vertical option list. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      disabled: PropTypes.bool,
      displayValue: PropTypes.string,
      label: PropTypes.node,
      value: PropTypes.string,
    })
  ),

  /** Width of the trigger and flyout. */
  minimumListWidth: PropTypes.oneOf(["large", "medium", "small", "xlarge", "xsmall"]),

  /**
   * Content to be displayed when there are list items and none have been selected yet.
   * If not specified, the display value of the first entry of the list is used instead.
   * noSelectionDisplayValue is rendered as SelectList's trigger.
   */
  noSelectionDisplayValue: PropTypes.string,

  /** Callback function to learn when a selection is made in "main" option list, and the parameters
   *  are the event object and the index of the selected option. */
  onChange: PropTypes.func,

  /** Function to learn when SelectList is clicked. */
  onClick: PropTypes.func,

  /** Function to learn when SelectList is opened and closed. */
  onRevealChange: PropTypes.func,

  /**
   * Array of items to provide additional action items at bottom of flyout. These values are
   * passed to HorizontalList component.
   */
  options: horizontalListPropTypes.items,

  /** Indicates this component should be immutable and ignore mouse and key events. */
  readOnly: PropTypes.bool,

  /** Indicates if the area is initially opened or closed. */
  revealed: areaPropTypes.revealed,

  /** Indicates whether border should be hidden when the element is not focused. */
  showBorder: PropTypes.bool,

  /** Value to be maintained by caller. This will define which option in vertical list is selected. */
  value: PropTypes.string,
};
